import React, { lazy, Suspense } from 'react';
import { HeroSection } from '@/components/home/HeroSection';
import { UpcomingTrainings } from '@/components/home/UpcomingTrainings';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';

// Lazy load below-fold sections
const AnnouncementBanners = lazy(() => import('@/components/home/AnnouncementBanners').then(m => ({ default: m.AnnouncementBanners })));
const AboutSection = lazy(() => import('@/components/home/AboutSection').then(m => ({ default: m.AboutSection })));
const TrainingSection = lazy(() => import('@/components/home/TrainingSection').then(m => ({ default: m.TrainingSection })));
const TestimonialsSection = lazy(() => import('@/components/home/TestimonialsSection').then(m => ({ default: m.TestimonialsSection })));
const CTASection = lazy(() => import('@/components/home/CTASection').then(m => ({ default: m.CTASection })));
const ExpertiseSection = lazy(() => import('@/components/home/ExpertiseSection').then(m => ({ default: m.ExpertiseSection })));
const AboutFAQ = lazy(() => import('@/components/about/AboutFAQ').then(m => ({ default: m.AboutFAQ })));
const AILevelSection = lazy(() => import('@/components/home/AILevelSection').then(m => ({ default: m.AILevelSection })));

export const HomePage = () => {
  const { language } = useLanguage();

  const imageObjectSchema = language === 'fr' ? {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "name": "Formation IA juridique pour avocats - 6 prérequis ChatGPT",
    "description": "Programme complet de formation à l'intelligence artificielle pour avocats : maîtrise de ChatGPT, Claude, Gemini avec focus sécurité et déontologie CNB",
    "contentUrl": "https://gaius-tech.com/images/formation-ia-juridique-avocat-prerequis-chatgpt.webp",
    "creator": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com"
    },
    "keywords": "formation IA juridique, ChatGPT avocat, Claude legal, IA droit, prompt engineering juridique, formation avocat CPF, FIF PL IA, Harvey AI alternative, hallucination IA vérification, automatisation cabinet avocat, juriste IA, CRFPA IA, Assas IA, Sorbonne droit IA, avocat GenZ tech, formation OPCO juridique, FCO avocat IA",
    "license": "https://gaius-tech.com/legal",
    "acquireLicensePage": "https://gaius-tech.com/training"
  } : undefined;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Gaius",
    "url": "https://gaius-tech.com",
    "description": language === 'fr' ?
    "Formation experte en intelligence artificielle pour avocats et juristes. Organisme certifié Qualiopi. Éligible aux financements OPCO." :
    "Expert artificial intelligence training for lawyers and legal professionals. Qualiopi-certified. Eligible for OPCO funding.",
    "foundingDate": "2024",
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Quality Certification",
      "name": "Qualiopi",
      "description": "Certification qualité Qualiopi - Éligible aux financements OPCO."
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "FR",
      "addressRegion": "Île-de-France",
      "addressLocality": "Paris"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "areaServed": ["FR", "EU", "GB"],
      "availableLanguage": ["French", "English"]
    }
  };

  const allStructuredData = imageObjectSchema
    ? [structuredData, imageObjectSchema]
    : structuredData;

  const seoTitle = language === 'fr' ?
  "Gaius | Formation IA Juridique pour Avocats et Juristes" :
  "Gaius | Legal AI Training for Lawyers and Legal Professionals";

  const seoDescription = language === 'fr' ?
  "Formation experte en IA pour avocats et juristes. Maîtrisez les outils et pratiques d'IA juridique adaptés à votre métier. Audit de stratégie IA pour cabinets." :
  "Expert AI training for lawyers and legal professionals. Master legal AI tools and practices tailored to your profession. AI strategy audit for law firms.";

  const seoKeywords = language === 'fr' ?
  "formation ia avocat, formation ia juriste, ia juridique, ia avocat, ia droit, audit ia cabinet, formation continue avocat" :
  "legal ai training, ai for lawyers, legal artificial intelligence, ai law, law firm ai audit, continuing legal education ai";

  return (
    <div className="min-h-screen">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://gaius-tech.com/"
        alternateUrlFr="https://gaius-tech.com/"
        alternateUrlEn="https://gaius-tech.com/"
        structuredData={allStructuredData} />

      <HeroSection />
      <UpcomingTrainings />
      <Suspense fallback={<div className="min-h-[400px]" />}>
        <AnnouncementBanners />
      </Suspense>
      <Suspense fallback={<div className="min-h-[500px]" />}>
        <AboutSection />
      </Suspense>
      <Suspense fallback={<div className="min-h-[500px]" />}>
        <TrainingSection />
      </Suspense>
      <Suspense fallback={<div className="min-h-[500px]" />}>
        <TestimonialsSection />
      </Suspense>
      <Suspense fallback={<div className="min-h-[400px]" />}>
        <ExpertiseSection />
      </Suspense>
      <Suspense fallback={<div className="min-h-[400px]" />}>
        <AboutFAQ />
      </Suspense>
      <Suspense fallback={<div className="min-h-[600px]" />}>
        <AILevelSection />
      </Suspense>
      <Suspense fallback={<div className="min-h-[300px]" />}>
        <CTASection />
      </Suspense>
    </div>);

};